import { Input } from '@breetometer/components';
import { DropdownSelect } from 'BreetComponents';
import { filterDisabledNetwork, getNetworkFee, type IDropDownItem, type NetworkType, type TokenType } from 'BreetHelpers';
import { useSavedWalletAddressApi } from 'BreetHooks';
import { memo, useCallback, useEffect, useMemo } from 'react';

import DropdownItemNetwork from './dropDownItem/DropdownItem.Network';
import DropdownItemToken from './dropDownItem/DropdownItem.Token';
import type { IWalletAddressFormComponentProps } from './WalletAddressForm.helpers';

const WalletAddressFormInputComponent = ({
	tokenOptions,
	walletInfoData,
	authSettings,
	formDataContextProps,
}: IWalletAddressFormComponentProps) => {
	const { isSavedWalletAddressesEmpty, getSavedWalletAddress, isLoadingSavedWalletAddresses } = useSavedWalletAddressApi();

	const {
		hookFormProps: { register, watch, setValue },
		getFieldError,
		setValueAndValidate,
	} = formDataContextProps;

	const formData = watch();
	const selectedToken = formData.token as TokenType | undefined;
	const selectedNetwork = formData.network as NetworkType | undefined;
	const receiverAddress = formData.receiverAddress as string | undefined;

	const networks = useMemo(() => {
		try {
			if (!selectedToken) return [];

			return walletInfoData?.data?.[selectedToken].networks ?? [];
		} catch {
			console.error('Networks not found');
			return [];
		}
	}, [selectedToken, walletInfoData?.data]);

	const networkOptions: IDropDownItem[] = useMemo(() => {
		try {
			if (!selectedToken) return [];

			const usdWithdrawalMeta = authSettings?.usdWithdrawalMeta;

			if (!usdWithdrawalMeta) return [];

			return networks.filter(filterDisabledNetwork(usdWithdrawalMeta, selectedToken)).map((option) => ({
				value: option.network,
				icon: option.avatar,
				code: `$ ${getNetworkFee(selectedToken, option.network, usdWithdrawalMeta)}`,
				name: option.network,
				id: option.network,
			}));
		} catch {
			console.error('Network options not found');
			return [];
		}
	}, [authSettings?.usdWithdrawalMeta, networks, selectedToken]);

	const handleInputSelect = useCallback(
		(key: 'token' | 'network') => (valueArg: string) => {
			setValueAndValidate(key, valueArg).catch(console.error);

			if (key === 'token') {
				setValue('network', '');
				setValue('receiverAddress', '');
				setValue('walletName', '');
			}

			if (key === 'network' && !!receiverAddress) {
				setValueAndValidate('receiverAddress', receiverAddress).catch(console.error);
			}
		},
		[setValueAndValidate, receiverAddress, setValue]
	);

	const hasReceiverAddressError = !!getFieldError('receiverAddress')?.message;

	const derivedWalletName = useMemo(() => {
		if (isSavedWalletAddressesEmpty || hasReceiverAddressError || !receiverAddress) {
			return '';
		}
		return getSavedWalletAddress({ address: receiverAddress, token: selectedToken })?.label ?? '';
	}, [isSavedWalletAddressesEmpty, hasReceiverAddressError, receiverAddress, getSavedWalletAddress, selectedToken]);

	useEffect(() => {
		if (!receiverAddress || (!derivedWalletName && !isLoadingSavedWalletAddresses)) {
			// Clear walletName when: address is empty, or address is set but not found
			// in the saved list and the query is not mid-fetch (genuine non-match).
			setValue('walletName', '', { shouldDirty: false });
		} else if (derivedWalletName) {
			// A matching saved address was found — auto-fill the label.
			setValue('walletName', derivedWalletName, { shouldDirty: true });
		}
		// If derivedWalletName is '' only because the query is loading, do nothing —
		// preserve the existing walletName until the fetch completes.
	}, [derivedWalletName, isLoadingSavedWalletAddresses, receiverAddress, setValue]);

	return (
		<>
			<DropdownSelect
				id='token'
				label='Select Coin'
				options={tokenOptions}
				onChange={handleInputSelect('token')}
				defaultOption={selectedToken}
				hideSearch
				CustomItemOption={DropdownItemToken}
				errorMessage={
					!walletInfoData || !authSettings?.usdWithdrawalMeta || tokenOptions.length
						? getFieldError('token')?.message
						: 'No coin is currently available. Please try again later or contact support.'
				}
			/>

			{!!selectedToken && (
				<>
					<DropdownSelect
						id='network'
						label='Destination Network'
						options={networkOptions}
						onChange={handleInputSelect('network')}
						defaultOption={selectedNetwork}
						hideSearch
						CustomItemOption={DropdownItemNetwork}
						errorMessage={
							!walletInfoData || !authSettings?.usdWithdrawalMeta || networkOptions.length
								? getFieldError('network')?.message
								: 'No network is currently available. Please try again later or contact support.'
						}
					/>
					<Input
						label="Enter Receiver's Address"
						id='receiverAddress'
						required
						widthType='full'
						autoComplete='off'
						{...register('receiverAddress')}
						errorMessage={getFieldError('receiverAddress')?.message}
					/>
					<Input
						label='Wallet Name e.g. Binance Wallet'
						id='walletAddress'
						required
						widthType='full'
						autoComplete='off'
						{...register('walletName')}
						errorMessage={getFieldError('walletName')?.message}
					/>
				</>
			)}
		</>
	);
};

const WalletAddressFormInput = memo(WalletAddressFormInputComponent);

WalletAddressFormInput.displayName = 'WalletAddressFormInput';

export default WalletAddressFormInput;
